import { Grid, OutlinedInput, Typography } from "@material-ui/core";
import { ChangeEvent, useState } from "react";
import { SelectCountryCode } from "../../../../components/select-country-code";
import { TextMask } from "../../../../shared/components";
import { CircleExclamationIcon, ZoomIcon } from "../../../../shared/icons";
import ToggleSlot from "../../toggle-slot/toggle-slot.component";
import { useStyles } from "./contact-form.styles";

interface LocationType {
  name: string;
  image?: JSX.Element;
  isBig?: boolean;
}

const locationTypes = {
  zoom: {
    name: "Zoom",
    image: <ZoomIcon />,
  } as LocationType,
  phone: {
    name: "Phone call",
    isBig: true,
  } as LocationType,
};

interface Props {
  onChangeDescription?: (value: string) => void;
  onChangeLocation?: (value: string) => void;
  description?: string;
  location?: string;
}

export default function ContactForm({
  description: propDescription,
  location: propLocation,
  onChangeDescription,
  onChangeLocation,
}: Props) {
  const classes = useStyles();

  const [isPhone, setIsPhone] = useState(
    !["Zoom", "zoom", "www"].includes(propLocation)
  );
  const [selected, setSelected] = useState(isPhone ? "Phone call" : "Zoom");
  const [locationCode, locationPhone] = propLocation.split(" ");
  const [phone, setPhone] = useState(locationPhone || "");
  const [callCode, setCallCode] = useState(
    locationCode == "Zoom" ? "1" : locationCode
  );
  const [description, setDescription] = useState(propDescription);

  const onLocationClick = (type: string) => {
    setIsPhone(type === locationTypes.phone.name);
    setSelected(type);
    onChangeLocation?.(
      type !== locationTypes.phone.name ? locationTypes.zoom.name : phone
    );
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isValid = value?.match(/\d+/g)?.join("").length === 10;
    setPhone(value);
    onChangeLocation(isValid ? `${callCode} ${value}` : "");
  };

  const isPhoneEmpty = (value: string) => {
    return value?.match(/\d+/g)?.join("").length === 0;
  };

  const handleCallCodeChange = (code: string) => {
    setCallCode(code);
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setDescription(value);
    onChangeDescription?.(value);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography className={classes.title}>
          Where do you want to meet?
        </Typography>
      </Grid>
      <Grid item container className={classes.locationsContainer}>
        {Object.values(locationTypes).map((location) => (
          <ToggleSlot
            key={location.name}
            isSelected={selected === location.name}
            isBig={location.isBig}
            content={location.image || location.name}
            onClick={() => onLocationClick(location.name)}
          />
        ))}
      </Grid>
      {!isPhone ? (
        <Grid item container className={classes.zoomTipContainer}>
          <div className={classes.zoomTipIcon}>
            <CircleExclamationIcon />
          </div>
          <Typography className={classes.zoomTip}>
            Your Mentor will send you the link of the meeting via email.
          </Typography>
        </Grid>
      ) : (
        <Grid item container className={classes.phoneContainer}>
          <SelectCountryCode
            valueDefault={callCode}
            className={classes.countryCallCode}
            onChange={handleCallCodeChange}
          />
          <OutlinedInput
            value={phone}
            placeholder="Phone number"
            className={
              isPhoneEmpty(phone)
                ? classes.phoneField
                : `${classes.phoneField} ${classes.phoneFieldEmpty}`
            }
            onChange={handlePhoneChange}
            inputComponent={TextMask}
          />
        </Grid>
      )}
      <Grid item xs={12} className={classes.descriptionContainer}>
        <Typography className={classes.title}>
          What do you want to cover?
        </Typography>
        <textarea
          minLength={2}
          maxLength={256}
          value={description}
          onChange={handleDescriptionChange}
          className={classes.description}
          placeholder={`Please shortly describe your needs, i.e.\n"I want help to create my budget".\n\nIt's mandatory to fill this information before booking a session.`}
        />
      </Grid>
    </Grid>
  );
}
